import { useEffect } from "react";
import { PLAN_DISPLAY_LABELS } from "./booking-plans";

/** Managed by RouteSeo / usePageSeo — do not set manually in components. */
export const SEO_MARKER = "data-avyug-seo";

export const SITE = {
  name: "Avyug",
  url: (process.env.REACT_APP_SITE_URL || "https://www.avyug.com").replace(/\/$/, ""),
  defaultTitle: "Feel Strong Again | Live Yoga & Therapy | Avyug",
  defaultDescription:
    "Return to your breath and rebuild from within. Live yoga and therapy shaped around you — calm, personal, and designed to help you feel fit in body and spirit.",
  ogImage: `${(process.env.REACT_APP_SITE_URL || "https://www.avyug.com").replace(/\/$/, "")}/favicon.png`,
};

/** Paths that should not appear in search results. */
const NOINDEX_PREFIXES = [
  "/admin",
  "/session/",
  "/join/",
  "/pay/",
  "/my-bookings",
  "/my-enquiries",
  "/profile",
  "/verify-email",
  "/auth",
];

const ROUTE_SEO = {
  "/": {
    title: "Feel Strong Again | Live Yoga & Therapy | Avyug",
    description:
      "Return to your breath. Rebuild from within. Live yoga and therapy that help you feel calm, strong, and at home in your body again.",
  },
  "/book": {
    title: "Begin Your Practice | Avyug",
    description:
      "Claim your moment. Live guidance shaped around you — one session, a month of rhythm, or a year of transformation.",
  },
  "/blog": {
    title: "Journal | Breath, Body & Vitality | Avyug",
    description:
      "Ideas to nourish your practice — breath, movement, and the quiet work of feeling fit again.",
  },
  "/services/single": {
    title: "Single Session | Avyug",
    description:
      "A single live session to land back in your body — personal, unhurried, and entirely yours.",
  },
  "/services/monthly": {
    title: "Monthly Rythm | Avyug",
    description:
      "Build a rhythm. Feel lighter and stronger week after week with steady live guidance.",
  },
  "/services/annual": {
    title: "Annual Care | Avyug",
    description:
      "Commit to yourself across every season. Lasting strength, calm, and vitality through live practice.",
  },
  "/careers/instructor": {
    title: "Yoga Instructor Careers | Avyug",
    description:
      "Apply to teach live online yoga with Avyug. Join our global team of certified instructors.",
  },
  "/careers/therapist": {
    title: "Yoga Therapist Careers | Avyug",
    description:
      "Apply to offer personalized online yoga therapy with Avyug. Help clients worldwide from your own schedule.",
  },
};

function isNoindexPath(pathname) {
  return NOINDEX_PREFIXES.some((p) => pathname === p || pathname.startsWith(p));
}

function pageUrl(path) {
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${SITE.url}${p === "/" ? "" : p}`;
}

/**
 * WebPage + BreadcrumbList JSON-LD for indexable routes.
 */
export function buildWebPageJsonLd({ title, description, path }) {
  const url = pageUrl(path);
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": `${url}#webpage`,
        "url": url,
        "name": title,
        "description": description,
        "isPartOf": { "@id": `${SITE.url}/#website` },
        "about": { "@id": `${SITE.url}/#business` },
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE.url },
          { "@type": "ListItem", "position": 2, "name": title, "item": url },
        ],
      },
    ],
  };
}

/**
 * Service offering JSON-LD for /services/* pages.
 */
export function buildServiceJsonLd({ title, description, path, serviceType }) {
  const url = pageUrl(path);
  const webGraph = buildWebPageJsonLd({ title, description, path })["@graph"] || [];
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        "@id": `${url}#service`,
        "name": title,
        "description": description,
        "url": url,
        "provider": { "@id": `${SITE.url}/#organization` },
        "areaServed": "Worldwide",
        "serviceType": serviceType || "Online yoga and therapy",
        "availableChannel": {
          "@type": "ServiceChannel",
          "serviceUrl": url,
          "serviceType": "Online",
        },
      },
      ...webGraph,
    ],
  };
}

function attachJsonLd(config, path) {
  if (!config || config.noindex) return config;
  const serviceMatch = path.match(/^\/services\/(single|monthly|annual)$/);
  if (serviceMatch) {
    const labels = {
      single: PLAN_DISPLAY_LABELS.single,
      monthly: PLAN_DISPLAY_LABELS.monthly,
      annual: PLAN_DISPLAY_LABELS.yearly,
    };
    return {
      ...config,
      jsonLd: buildServiceJsonLd({
        title: config.title,
        description: config.description,
        path,
        serviceType: labels[serviceMatch[1]] || "Online yoga",
      }),
    };
  }
  return { ...config, jsonLd: buildWebPageJsonLd({ title: config.title, description: config.description, path }) };
}

/**
 * @param {string} pathname
 * @returns {{ title: string, description: string, path: string, noindex?: boolean, jsonLd?: object } | null}
 */
export function getRouteSeo(pathname) {
  const path = pathname || "/";
  if (path.startsWith("/blog/") && path !== "/blog") return null;

  if (isNoindexPath(path)) {
    return {
      title: SITE.name,
      description: SITE.defaultDescription,
      path,
      noindex: true,
    };
  }

  const exact = ROUTE_SEO[path];
  if (exact) return attachJsonLd({ ...exact, path }, path);

  const serviceMatch = path.match(/^\/services\/(single|monthly|annual)$/);
  if (serviceMatch) return attachJsonLd({ ...ROUTE_SEO[path], path }, path);

  const careerMatch = path.match(/^\/careers\/(instructor|therapist)$/);
  if (careerMatch) return attachJsonLd({ ...ROUTE_SEO[path], path }, path);

  return attachJsonLd(
    {
      title: SITE.defaultTitle,
      description: SITE.defaultDescription,
      path,
    },
    path
  );
}

function upsertMeta(name, content, { property = false } = {}) {
  const key = property ? "property" : "name";
  let el = document.head.querySelector(`meta[${key}="${name}"][${SEO_MARKER}]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(key, name);
    el.setAttribute(SEO_MARKER, "true");
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"][${SEO_MARKER}]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    el.setAttribute(SEO_MARKER, "true");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function removeManagedJsonLd() {
  document.head.querySelectorAll(`script[type="application/ld+json"][${SEO_MARKER}]`).forEach((n) => n.remove());
}

/**
 * Updates document title, meta tags, canonical URL, and optional JSON-LD for the current SPA route.
 * @param {{ title: string, description: string, path?: string, image?: string, noindex?: boolean, jsonLd?: object }} opts
 */
export function usePageSeo({ title, description, path = "", image, noindex = false, jsonLd, enabled = true }) {
  const jsonLdKey = jsonLd ? JSON.stringify(jsonLd) : "";

  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;
    const frameId = requestAnimationFrame(() => {
      if (cancelled) return;

      const canonicalPath = path.startsWith("/") ? path : `/${path}`;
      const canonical = `${SITE.url}${canonicalPath === "/" ? "" : canonicalPath}`;
      const ogImage = image || SITE.ogImage;

      document.title = title;
      upsertMeta("description", description);
      upsertMeta("robots", noindex ? "noindex, nofollow" : "index, follow");
      upsertMeta("og:title", title, { property: true });
      upsertMeta("og:description", description, { property: true });
      upsertMeta("og:type", "website", { property: true });
      upsertMeta("og:url", canonical, { property: true });
      upsertMeta("og:image", ogImage, { property: true });
      upsertMeta("og:site_name", SITE.name, { property: true });
      upsertMeta("twitter:card", "summary_large_image");
      upsertMeta("twitter:title", title);
      upsertMeta("twitter:description", description);
      upsertMeta("twitter:image", ogImage);
      upsertLink("canonical", canonical);

      removeManagedJsonLd();
      if (jsonLd) {
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.setAttribute(SEO_MARKER, "true");
        script.textContent = JSON.stringify(jsonLd);
        document.head.appendChild(script);
      }
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(frameId);
      removeManagedJsonLd();
    };
  }, [title, description, path, image, noindex, jsonLdKey, enabled, jsonLd]);
}

/**
 * @param {{ headline: string, description: string, datePublished?: string, image?: string, path: string }} article
 */
export function buildArticleJsonLd({ headline, description, datePublished, image, path }) {
  const url = `${SITE.url}${path}`;
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline,
    description,
    url,
    mainEntityOfPage: url,
    image: image || SITE.ogImage,
    datePublished: datePublished || undefined,
    publisher: {
      "@type": "Organization",
      name: SITE.name,
      logo: { "@type": "ImageObject", url: SITE.ogImage },
    },
  };
}
